import * as yup from 'yup';

import { useTranslations } from 'hooks/useTranslations';
import { countEditorCharacters } from 'components/organisms/HtmlSlate';
import {
  EgoodReasonValues,
  EsafeStorageValues,
  ILicenceTemplateResponse,
} from 'models/licenceConfigurator';
import { isUUID } from 'utils/uuid';
import { isStringEmpty } from 'utils/string';
import { ILegalEntityData } from 'components/organisms/LegalEntityDetails';
import { IOrgLegalEntityData } from 'components/organisms/OrgLegalEntityDetails';

const useDocumentFormSchema = () => {
  const { t } = useTranslations();
  const schema = yup.object({
    name: yup
      .string()
      .required(t('general.field.validation.required'))
      .max(255, t('general.field.validation.maxCharacters').replace('{0}', '255'))
      .trim(),
    weaponLicenceDocumentType: yup.string().required(t('general.field.validation.required')),
    comment: yup
      .string()
      .max(255, t('general.field.validation.maxCharacters').replace('{0}', '255'))
      .trim(),
  });
  return { schema };
};

const useFormSchema = (
  template: ILicenceTemplateResponse,
  legalEntity: ILegalEntityData | IOrgLegalEntityData | null,
  isIndividualType: boolean,
  getCurrentWeaponCount: () => number = () => 0,
) => {
  const { t } = useTranslations();
  const schema = yup.object({
    conditions: yup.object({
      competencyUniqueId: yup
        .string()
        .nullable()
        .test('required', t('general.field.validation.required'), (value) => {
          if (template.conditions.competency) return isUUID(value);

          return true;
        }),

      carry: yup
        .string()
        .nullable()
        .test('required', t('general.field.validation.required'), (value) => {
          if (template.conditions.carryConditions) return isUUID(value);

          return true;
        }),

      areaOfValidityUniqueId: yup
        .string()
        .nullable()
        .test('required', t('general.field.validation.required'), (value) => {
          if (!template.conditions.specificCountryArea) return isUUID(value);

          return true;
        }),

      weaponCategorisationUniqueId: yup
        .string()
        .nullable()
        .test('required', t('general.field.validation.required'), (value) => {
          if (template.conditions.weaponCategory) return isUUID(value);

          return true;
        }),

      weaponNumberLimit: yup
        .number()
        .nullable()
        .test('required', t('general.field.validation.required'), (value) => {
          if (template.conditions.weaponNumberLimit)
            return value !== undefined && value !== null && Number(value) > 0;

          return true;
        })
        .test(
          'weapon-limit-exceeded',
          t('licence.field.validation.weaponNumberLimitExceeded'),
          (value) => {
            if (template.conditions.weaponNumberLimit && value) {
              return Number(value) >= getCurrentWeaponCount();
            }
            return true;
          },
        ),

      additionalConditions: yup
        .string()
        .nullable()
        .test('required', t('general.field.validation.required'), (value) => {
          if (template.conditions.additionalConditions)
            return countEditorCharacters(value) !== 0;

          return true;
        })
        .test(
          'max-chars',
          t('general.field.validation.maxCharacters').replace('{0}', '1000'),
          (value) => {
            if (template.conditions.additionalConditions)
              return countEditorCharacters(value) <= 1000;

            return true;
          },
        ),

      hasSafeStorage: yup
        .boolean()
        .nullable()
        .test('required', t('general.field.validation.required'), (value) => {
          if (
            template.conditions.safeStorage === EsafeStorageValues.Text ||
            template.conditions.safeStorage === EsafeStorageValues.Object
          )
            return value !== undefined && value !== null;
          return true;
        }),

      safeStorages: yup
        .array()
        .nullable()
        .test('required', t('general.field.validation.atLeastOneRequired'), (value, obj) => {
          if (
            template.conditions.safeStorage === EsafeStorageValues.Object &&
            obj.parent.hasSafeStorage
          ) {
            return Array.isArray(value) && value.length > 0;
          }
          return true;
        }),

      safeStorageDescription: yup
        .string()
        .nullable()
        .test(
          'editor-validation',
          t('general.field.validation.maxCharacters').replace('{0}', '1000'),
          (value) => countEditorCharacters(value) <= 1000,
        )
        .test('required', t('general.field.validation.required'), (value, obj) => {
          if (
            template.conditions.safeStorage === EsafeStorageValues.Text ||
            (template.conditions.safeStorage === EsafeStorageValues.Object &&
              !obj.parent.hasSafeStorage)
          )
            return !isStringEmpty(value);
          return true;
        }),

      goodReasonUniqueIds: yup
        .object()
        .nullable()
        .test('required', t('general.field.validation.required'), (values) => {
          if (
            Array.isArray(template?.conditions?.goodReasonDictionaryIds) &&
            template.conditions.goodReasonDictionaryIds.length > 0
          )
            return !!values && Object.values(values).some((val) => val === true);

          return true;
        }),
      goodReason: yup
        .string()
        .nullable()
        .max(1000, t('general.field.validation.maxCharacters').replace('{0}', '1000'))
        .test('required', t('general.field.validation.required'), (value) => {
          if (template.conditions.goodReason === EgoodReasonValues.Text)
            return !!value && !isStringEmpty(value);

          return true;
        }),
    }),
    additionalInformation: yup
      .string()
      .nullable()
      .when('$licence.documentInformation.additionalConditions', (val, schema) =>
        val !== undefined && val !== null && val.toString() !== ''
          ? schema
            .required(t('general.field.validation.required'))
            .max(1000, t('general.field.validation.maxCharacters').replace('{0}', '1000'))
            .trim()
          : schema.nullable().trim(),
      ),
    suitabilityAssessmentPassed: yup
      .boolean()
      .test(
        'required-true',
        t('general.field.validation.required'),
        (value) => value === true,
      ),
  });
  return { schema };
};

export { useDocumentFormSchema, useFormSchema };
